import React, { useState, useMemo } from 'react';
import { PieChart } from 'lucide-react';
import CalculatorLayout from '../../components/calculators/CalculatorLayout';
import { HeightInput, WeightInput } from '../../components/calculators/MeasureInputs';
import { NumberField, SelectField, GenderField } from '../../components/calculators/FormFields';
import { EmptyResult, PrimaryResult, StatRow } from '../../components/calculators/ResultCard';
import {
  calculateBMR, calculateTDEE, calculateMacros, getCalorieTargets,
  ACTIVITY_FACTORS, MACRO_SPLITS
} from '../../lib/calculatorUtils';

const activityOptions = Object.entries(ACTIVITY_FACTORS).map(([k, v]) => ({
  value: k, label: `${v.label}: ${v.desc}`
}));

const goalOptions = [
  { value: 'fat_loss', label: 'Fat Loss (deficit)' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'muscle_gain', label: 'Muscle Gain (surplus)' }
];

const MacroCalculatorPage = () => {
  const [unit, setUnit] = useState('metric');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('female');
  const [heightCm, setHeightCm] = useState('');
  const [weightKg, setWeightKg] = useState('');
  const [activity, setActivity] = useState('moderate');
  const [goal, setGoal] = useState('maintenance');

  const result = useMemo(() => {
    const a = parseInt(age, 10);
    const h = parseFloat(heightCm);
    const w = parseFloat(weightKg);
    if (!a || !h || !w || a < 10 || h < 50 || w < 20) return null;
    const bmr = calculateBMR({ gender, weightKg: w, heightCm: h, age: a });
    const tdee = calculateTDEE(bmr, activity);
    const targets = getCalorieTargets(tdee);
    let calories;
    if (goal === 'fat_loss') calories = targets.moderate;
    else if (goal === 'muscle_gain') calories = targets.muscleGain;
    else calories = targets.maintenance;
    const macros = calculateMacros(calories, goal);
    return { bmr: Math.round(bmr), tdee: Math.round(tdee), calories, macros };
  }, [age, gender, heightCm, weightKg, activity, goal]);

  const inputs = (
    <div className="space-y-5" data-testid="macro-inputs">
      <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">Build Your Macros</h2>
      <p className="text-slate-400 text-sm mb-5">Get a complete macro breakdown for your goal.</p>
      <div className="grid grid-cols-2 gap-3">
        <NumberField id="m-age" label="Age" value={age} onChange={setAge} placeholder="e.g. 28" suffix="yrs" min={10} max={120} testId="macro-age-input" required />
        <GenderField value={gender} onChange={setGender} testIdPrefix="macro-gender" />
      </div>
      <HeightInput unit={unit} onUnitChange={setUnit} heightCm={heightCm} onHeightCmChange={setHeightCm} />
      <WeightInput unit={unit} onUnitChange={setUnit} weightKg={weightKg} onWeightKgChange={setWeightKg} />
      <SelectField id="m-activity" label="Activity Level" value={activity} onChange={setActivity} options={activityOptions} testId="macro-activity-select" required />
      <SelectField id="m-goal" label="Goal" value={goal} onChange={setGoal} options={goalOptions} testId="macro-goal-select" required />
    </div>
  );

  const splitLabel = MACRO_SPLITS[goal]?.label || 'Maintenance';
  const macroSegments = result ? [
    { key: 'protein', color: 'bg-rose-500', label: 'Protein', g: result.macros.protein.grams, kcal: result.macros.protein.calories, pct: result.macros.protein.percent },
    { key: 'carbs', color: 'bg-amber-500', label: 'Carbs', g: result.macros.carbs.grams, kcal: result.macros.carbs.calories, pct: result.macros.carbs.percent },
    { key: 'fat', color: 'bg-emerald-500', label: 'Fats', g: result.macros.fat.grams, kcal: result.macros.fat.calories, pct: result.macros.fat.percent }
  ] : [];

  const resultNode = result ? (
    <div className="flex flex-col h-full">
      <PrimaryResult label={`Daily Target: ${splitLabel}`} value={result.calories.toLocaleString()} unit="kcal/day" accent="from-amber-500 to-rose-500" testId="macro-result-calories" />

      {/* Macro bar */}
      <div className="mt-8">
        <div className="flex h-4 rounded-full overflow-hidden border border-slate-700">
          {macroSegments.map((s) => (
            <div key={s.key} className={s.color} style={{ width: `${s.pct}%` }} aria-hidden="true" data-testid={`macro-bar-${s.key}`}></div>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-3 mt-5">
          {macroSegments.map((s) => (
            <div key={s.key} className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-3 text-center" data-testid={`macro-card-${s.key}`}>
              <div className="flex items-center justify-center gap-2 mb-1">
                <span className={`w-2.5 h-2.5 rounded-full ${s.color}`} aria-hidden="true"></span>
                <p className="text-slate-300 text-xs font-medium">{s.label}</p>
              </div>
              <p className="text-white font-bold text-lg sm:text-xl">{s.g}<span className="text-slate-500 text-xs ml-0.5">g</span></p>
              <p className="text-slate-500 text-[10px] mt-0.5">{s.pct}% · {s.kcal} kcal</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 space-y-1">
        <StatRow label="BMR" value={result.bmr.toLocaleString()} unit="kcal" testId="macro-bmr" />
        <StatRow label="TDEE" value={result.tdee.toLocaleString()} unit="kcal" testId="macro-tdee" />
      </div>

      <p className="text-slate-500 text-xs mt-6 leading-relaxed">
        Macro splits used: Fat Loss 35/35/30 · Muscle Gain 30/45/25 · Maintenance 30/40/30 (P/C/F).
      </p>
    </div>
  ) : (
    <EmptyResult message="Fill in your profile and goal to see your full macro breakdown." />
  );

  return (
    <CalculatorLayout
      slug="macro"
      title="Macro Calculator"
      subtitle="Get a complete macronutrient breakdown of protein, carbs and fats for your fat loss, muscle gain or maintenance goal."
      metaDescription="Macro Calculator from TCUBE FITNESS to calculate daily protein, carbs and fat targets in grams for your specific goal."
      icon={PieChart}
      accentGradient="from-amber-500 to-rose-500"
      accentColor="text-amber-400"
      badgeBg="bg-amber-500/10"
      badgeBorder="border-amber-500/30"
      inputs={inputs}
      result={resultNode}
      formulaText={
        <>
          <p><strong className="text-slate-300">Calorie conversions:</strong> 1 g protein = 4 kcal · 1 g carbs = 4 kcal · 1 g fat = 9 kcal</p>
          <p><strong className="text-slate-300">Splits used:</strong></p>
          <p>Fat Loss: 35% protein / 35% carbs / 30% fat</p>
          <p>Muscle Gain: 30% protein / 45% carbs / 25% fat</p>
          <p>Maintenance: 30% protein / 40% carbs / 30% fat</p>
        </>
      }
    />
  );
};

export default MacroCalculatorPage;
